import { round2Decimals } from "../common/util/functions";
import { PaymentStatus, TransactionType } from "./enums";
import { PaginatedResult } from "./pagination";
import { SettingsPayType } from "./pricing";
import { TicketPaymentItem } from "./ticket";

export interface QueueStatusDto {
    downloadUrl?: string;
    status: string;
}

export interface UpsertAccountsReceivableDto {
    id?: string | undefined;
    creditLimit: number | undefined;
    balanceDue: number | undefined;
    accountNumber: string;
    isActive: boolean;
}

export class UpsertAccountsReceivableDto implements UpsertAccountsReceivableDto {
    constructor(init?: AccountsReceivableFormValues) {
        if(init){
            this.id = init.id;
            this.creditLimit = init.creditLimit;
            this.balanceDue = init.balanceDue;
            this.accountNumber = init.accountNumber ?? "";
            this.isActive = init.isActive.toString() === "true";
        }
    }
}

export interface OutstandingTicketResultDto {
    tickets: PaginatedResult<OutstandingTicketDto[]>;
    totalAmountDue: number;
}

export interface OutstandingTicketDto {
    ticketId?: string;
    ticketNumber?: number;
    ticketDate: Date;
    description: string;
    ticketTotal: number;
    balanceDue: number;
}

export interface PrintARStatementTicketDto {
    ticketId?: string;
    ticketDate: Date;
    daysOutstanding: number;
    ticketNumber?: number;
    description: string;
    ticketTotal: number;
    payments: number;
    balanceDue: number;
}

export interface ApplyARToOutstandingTicketRequestItemDto {
    ticketId: string;
    ticketNumber: number;
    ticketDate: Date;
    description: string;
    ticketTotal: number;
    balanceDue: number;
    amountToPay: number;
}

export interface ApplyARToOutstandingTicketRequestDto {
    customerId?: string;
    accountsReceivableId?: string;
    referenceNumber: string;
    totalPaymentAmount?: number;
    commonTicketPayment: TicketPaymentItem;
    items: ApplyARToOutstandingTicketRequestItemDto[];
}

export class ApplyARToOutstandingTicketRequestDto implements ApplyARToOutstandingTicketRequestDto {
    constructor(init?: ApplyARToOutstandingTicketFormValues) {
        if (init) {
            this.customerId = init.customerId;
            this.accountsReceivableId = init.accountsReceivableId;
            this.referenceNumber = init.referenceNumber;
            this.totalPaymentAmount = init.totalPaymentAmount;
            this.items = init.items;
            this.commonTicketPayment = {
                status: init.paymentType?.isPaymentProcessor ? PaymentStatus.Created : PaymentStatus.Succeeded,
                paymentTypeId: init.paymentTypeId,
                transactionType: TransactionType.Sale,
                refundedPaymentId: undefined,
                amount: round2Decimals(Number(init.totalPaymentAmount ?? 0)),
                createdDate: new Date(),
                ticketCardTransactionId: undefined,
                isAdd: false,
                isCredit: false,
                refunded: false,
                refundedAmount: 0.0
            };
        }
    }
}

export interface ApplyARToOutstandingTicketFormValues {
    customerId?: string;
    accountsReceivableId?: string;
    referenceNumber: string;
    totalPaymentAmount?: number;
    paymentTypeId?: string;
    paymentType?: SettingsPayType;
    items: ApplyARToOutstandingTicketRequestItemDto[];
    error?: string;
}


export interface AccountsReceivableIndexItemDto {
    id: string;
    customerId: string;
    companyName?: string;
    customerFirstName?: string;
    customerMiddleName?: string;
    customerLastName?: string;
    accountNumber: string;
    creditLimit: number;
    availableCredit: number;
    balanceDue: number;
}

export interface ExpandedARIndexItemTicketDto {
    ticketId: string;
    ticketNumber: number;
    date: Date | undefined;
    description?: string;
    ticketTotal?: number;
    creditApplied?: number;
    balanceDue?: number;
}

export interface SettingsAccountsReceivableDTO {
    id?: string | undefined;
    isActive: boolean | undefined;
    defaultMaxCreditLimit: number | undefined;
    applyToAll: boolean | undefined;
    showMiddleDisclaimer: boolean | undefined;
}

export interface SettingsAccountsReceivableFormValues extends SettingsAccountsReceivableDTO {
    error?: string | undefined;
}


export interface AccountsReceivableFormValues {
    id?: string;
    customerId: string;
    creditLimit: number | undefined;
    balanceDue: number | undefined;
    accountNumber?: string;
    isActive: boolean;
    showMiddleDisclaimer: boolean;
}

export class AccountsReceivableFormValues implements AccountsReceivableFormValues {
    constructor(init?: AccountsReceivableFormValues | AccountsReceivable) {
        if (init) {
            this.id = init.id;
            this.customerId = init.customerId;
            this.creditLimit = init.creditLimit;
            this.balanceDue = init.balanceDue;
            this.accountNumber = init.accountNumber;
            this.isActive = init.isActive;
            this.showMiddleDisclaimer = init.showMiddleDisclaimer ?? false;
        }
        else {
            this.isActive = false;
        }
    }
}


export interface AccountsReceivable {
    id: string;
    customerId: string;
    creditLimit: number;
    balanceDue: number;
    accountNumber: string;
    creditLeftToUse: number;
    isActive: boolean;
    showMiddleDisclaimer?: boolean | undefined;
}

export enum FilterByARIndex {
    All,
    Paid,
    NotPaid
}

export interface SearchARIndexDto {
    customerName: string;
    filterBy?: FilterByARIndex;
}

export interface AccountsReceivableReportItemDto {
    accountsReceivableId: string;
    ticketId?: string | undefined;
    customerId?: string | undefined;
    ticketAccountsReceivableHistoryId?: string | undefined;
    ticketNumber?: number | undefined;
    ticketDate?: Date | undefined;
    accountNumber?: string | undefined;
    companyName?: string | undefined;
    customerName?: string | undefined;
    description?: string | undefined;
    creditLimit?: number | undefined;
    createdDate?: string | undefined;
    total: number;
    balanceDue: number;
}


export interface AccountsReceivableAgingReportItemDto {
    accountsReceivableId: string;
    ticketId: string | undefined;
    customerId: string | undefined;
    ticketAccountsReceivableHistoryId: string | undefined;
    customerName?: string | undefined;
    companyName?: string | undefined;
    invoiceDate: Date | undefined;
    invoiceNumber: number | undefined;
    daysOutstanding: number;
    oneToThirtyPastBalanceDue: number | undefined;
    thirtyToSixtyPastBalanceDue: number | undefined;
    sixtyToNinetyPastBalanceDue: number | undefined;
    ninetyPlusPastBalanceDue: number | undefined;
    daysPastDue: number;
    paymentsReceived?: number | undefined;
    balanceDue: number | undefined;
}
